/**
 * Horizontal card carousel used by the review and feature-card sections.
 * Progressive enhancement: without JS the track is still natively scrollable,
 * so the only thing lost is the prev/next buttons.
 *
 * Two opt-in behaviours, both off unless the markup asks for them, so the
 * sections sharing this element keep their existing behaviour:
 *   loop                     — wrap from the last page to the first and back,
 *                              instead of disabling the button at each end
 *   [data-ms-carousel-dots]  — render one dot per page, click to jump
 */
class MsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ms-carousel-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-ms-carousel-prev]');
    this.next = this.querySelector('[data-ms-carousel-next]');
    this.dots = this.querySelector('[data-ms-carousel-dots]');
    this.loop = this.hasAttribute('loop');

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));
    this.track.addEventListener('scroll', () => this.syncControls(), { passive: true });

    this.buildDots();
    this.syncControls();
    this.resizeObserver = new ResizeObserver(() => {
      this.buildDots();
      this.syncControls();
    });
    this.resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get gap() {
    const value = parseFloat(getComputedStyle(this.track).columnGap);
    return Number.isFinite(value) ? value : 16;
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return 0;
    return first.getBoundingClientRect().width + this.gap;
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get pageCount() {
    if (this.step <= 0) return 1;
    return Math.max(1, Math.round(this.maxScroll / this.step) + 1);
  }

  get pageIndex() {
    if (this.step <= 0) return 0;
    return Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / this.step));
  }

  page(direction) {
    const step = this.step;
    if (step <= 0) return;

    const target = this.track.scrollLeft + step * direction;

    // Wrap rather than stop. The 1px tolerance is because sub-pixel rounding
    // means scrollLeft rarely lands exactly on 0 or on the maximum.
    //
    // Wraps jump instead of animating. Partly because scroll-snap-type:
    // mandatory aborts a smooth programmatic scroll aimed at the end edge —
    // wrapping backwards did nothing at all — and partly because racing the
    // whole track backwards reads as a glitch rather than as a step.
    if (this.loop && direction > 0 && this.track.scrollLeft >= this.maxScroll - 1) {
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      return;
    }
    if (this.loop && direction < 0 && this.track.scrollLeft <= 1) {
      this.track.scrollTo({ left: this.maxScroll, behavior: 'instant' });
      return;
    }

    this.track.scrollTo({ left: target });
  }

  buildDots() {
    if (!this.dots) return;

    const count = this.pageCount;
    if (this.dots.childElementCount === count) return;

    this.dots.replaceChildren();
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ms-carousel__dot';
      dot.setAttribute('aria-label', `Go to page ${i + 1} of ${count}`);
      dot.addEventListener('click', () => this.track.scrollTo({ left: this.step * i }));
      this.dots.append(dot);
    }
  }

  syncControls() {
    if (this.prev && this.next) {
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= this.maxScroll - 1;
      // Looping carousels always have somewhere to go.
      this.prev.disabled = this.loop ? false : atStart;
      this.next.disabled = this.loop ? false : atEnd;
    }

    this.toggleAttribute('data-no-overflow', this.maxScroll <= 1);

    if (this.dots) {
      const active = this.pageIndex;
      [...this.dots.children].forEach((dot, i) => {
        dot.toggleAttribute('data-active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }
  }
}

if (!customElements.get('ms-carousel')) {
  customElements.define('ms-carousel', MsCarousel);
}
